"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  setSandboxInterests,
  setSandboxOnboardingSeen,
} from "@/lib/sandbox-onboarding";

export type InterestValue = "premium_collector" | "directional_trader";

/** The wheel cycle: Sell Put → Own Shares → Sell Call → Cash, with premium ticks. */
function WheelIllustration() {
  return (
    <svg viewBox="0 0 200 150" className="h-full w-full" aria-hidden="true">
      <circle cx="100" cy="78" r="40" fill="none" strokeWidth="1.5" className="stroke-zinc-300 dark:stroke-zinc-600" />
      {/* Cycle arrowheads */}
      <polygon points="124.8,109.8 131.8,107.0 127.6,102.8" className="fill-zinc-400 dark:fill-zinc-500" />
      <polygon points="75.2,46.2 72.4,53.2 68.2,49.0" className="fill-zinc-400 dark:fill-zinc-500" />
      {/* Stage nodes */}
      <circle cx="100" cy="38" r="5" strokeWidth="1.5" className="fill-emerald-500 stroke-emerald-600 dark:stroke-emerald-400" />
      <circle cx="140" cy="78" r="5" strokeWidth="1.5" className="fill-zinc-100 dark:fill-zinc-800 stroke-zinc-400 dark:stroke-zinc-500" />
      <circle cx="100" cy="118" r="5" strokeWidth="1.5" className="fill-emerald-500 stroke-emerald-600 dark:stroke-emerald-400" />
      <circle cx="60" cy="78" r="5" strokeWidth="1.5" className="fill-zinc-100 dark:fill-zinc-800 stroke-zinc-400 dark:stroke-zinc-500" />
      {/* Center premium */}
      <circle cx="100" cy="78" r="15" strokeWidth="1.5" className="fill-zinc-100/80 dark:fill-zinc-800/80 stroke-zinc-300 dark:stroke-zinc-600" />
      <text x="100" y="83" textAnchor="middle" fontSize="13" fontWeight="600" className="fill-emerald-500">$</text>
      {/* Stage labels */}
      <text x="100" y="27" textAnchor="middle" fontSize="8" fontWeight="500" className="fill-zinc-600 dark:fill-zinc-300">Sell Put</text>
      <text x="149" y="81" textAnchor="start" fontSize="8" fontWeight="500" className="fill-zinc-500 dark:fill-zinc-400">Own Shares</text>
      <text x="100" y="134" textAnchor="middle" fontSize="8" fontWeight="500" className="fill-zinc-600 dark:fill-zinc-300">Sell Call</text>
      <text x="51" y="81" textAnchor="end" fontSize="8" fontWeight="500" className="fill-zinc-500 dark:fill-zinc-400">Cash</text>
      {/* Premium collected on both option sales */}
      <text x="119" y="38" textAnchor="start" fontSize="7" fontWeight="600" className="fill-emerald-500">+$</text>
      <text x="119" y="122" textAnchor="start" fontSize="7" fontWeight="600" className="fill-emerald-500">+$</text>
    </svg>
  );
}

/** Simplified GEX profile: call bars right, put bars left, dashed spot line. */
function GexIllustration() {
  const callBars = [
    { y: 20, w: 18 },
    { y: 33, w: 42 },
    { y: 46, w: 68 },
    { y: 59, w: 36 },
    { y: 72, w: 14 },
  ];
  const putBars = [
    { y: 85, w: 12 },
    { y: 98, w: 30 },
    { y: 111, w: 62 },
    { y: 124, w: 38 },
  ];
  return (
    <svg viewBox="0 0 200 150" className="h-full w-full" aria-hidden="true">
      {/* Strike axis */}
      <line x1="100" y1="12" x2="100" y2="138" strokeWidth="1" className="stroke-zinc-300 dark:stroke-zinc-600" />
      {/* Call GEX (right) */}
      {callBars.map((b) => (
        <rect key={`c-${b.y}`} x="101" y={b.y - 4} width={b.w} height="8" rx="1.5" fillOpacity="0.85" className="fill-emerald-500" />
      ))}
      {/* Put GEX (left) */}
      {putBars.map((b) => (
        <rect key={`p-${b.y}`} x={99 - b.w} y={b.y - 4} width={b.w} height="8" rx="1.5" fillOpacity="0.8" className="fill-red-500" />
      ))}
      {/* Spot price */}
      <line x1="20" y1="78.5" x2="180" y2="78.5" strokeWidth="1.2" strokeDasharray="4 3" className="stroke-zinc-400 dark:stroke-zinc-500" />
      <text x="180" y="74" textAnchor="end" fontSize="7" fontWeight="500" className="fill-zinc-500 dark:fill-zinc-400">Spot</text>
      <text x="176" y="21" textAnchor="start" fontSize="7.5" fontWeight="600" className="fill-emerald-600 dark:fill-emerald-400">Calls</text>
      <text x="24" y="139" textAnchor="end" fontSize="7.5" fontWeight="600" className="fill-red-600 dark:fill-red-400">Puts</text>
    </svg>
  );
}

const INTEREST_OPTIONS: {
  value: InterestValue;
  label: string;
  tagline: string;
  description: string;
  Illustration: () => JSX.Element;
}[] = [
  {
    value: "premium_collector",
    label: "Premium Collector",
    tagline: "Income",
    description:
      "Wheel, covered calls, cash-secured puts, and long-term portfolio building.",
    Illustration: WheelIllustration,
  },
  {
    value: "directional_trader",
    label: "Directional Trader",
    tagline: "Day Trading",
    description:
      "GEX, Options Flow, GEX Heatmap for day trading and swing trading.",
    Illustration: GexIllustration,
  },
];

interface OnboardingInterestsModalProps {
  open: boolean;
  onboardingInterests: string[];
  onInterestsSaved: (interests: string[]) => void;
  onComplete: (interests: string[]) => void;
  sandbox?: boolean;
}

export function OnboardingInterestsModal({
  open,
  onboardingInterests,
  onInterestsSaved,
  onComplete,
  sandbox = false,
}: OnboardingInterestsModalProps) {
  const [selected, setSelected] = useState<string[]>(onboardingInterests);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setSelected(onboardingInterests.length > 0 ? onboardingInterests : []);
    }
  }, [open, onboardingInterests]);

  const toggleInterest = (value: string) => {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((x) => x !== value) : [...prev, value]
    );
  };

  const handleContinue = async () => {
    if (selected.length === 0) return;

    if (sandbox) {
      setSandboxInterests(selected);
      setSandboxOnboardingSeen();
      onInterestsSaved(selected);
      onComplete(selected);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/user/onboarding", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ interests: selected }),
      });
      if (!res.ok) {
        console.error("Failed to save onboarding interests, proceeding anyway");
      }
    } catch (e) {
      console.error("Error saving onboarding interests:", e);
    } finally {
      setLoading(false);
    }
    onInterestsSaved(selected);
    onComplete(selected);
  };

  const cardContent = (
    <>
      <div className="h-1.5 rounded-t-lg bg-gradient-to-r from-transparent via-zinc-300 dark:via-zinc-600 to-transparent shadow-[0_2px_8px_rgba(161,161,170,0.25)]" />
      <div className="rounded-b-lg border border-t-0 border-border/50 bg-background shadow-lg">
        <div className="p-8 space-y-7">
          <div className="space-y-1.5">
            <h2 className="text-xl font-semibold tracking-tight">
              How do you trade?
            </h2>
            <p className="text-sm text-muted-foreground">
              Pick one or both.
            </p>
          </div>

          {/* Two-column card grid */}
          <div className="grid grid-cols-2 gap-3">
            {INTEREST_OPTIONS.map((opt) => {
              const isSelected = selected.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => toggleInterest(opt.value)}
                  className={cn(
                    "relative flex flex-col items-start rounded-xl border p-4 text-left transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400/60 dark:focus-visible:ring-zinc-500/60",
                    isSelected
                      ? "border-zinc-400/70 dark:border-zinc-500/60 bg-gradient-to-br from-zinc-100/90 via-zinc-50 to-zinc-100/60 dark:from-zinc-800/60 dark:via-zinc-900/50 dark:to-zinc-800/50 ring-1 ring-zinc-300/60 dark:ring-zinc-600/50 shadow-[0_2px_12px_rgba(161,161,170,0.14)]"
                      : "border-border/60 bg-muted/20 hover:border-zinc-300/60 dark:hover:border-zinc-700/60 hover:bg-zinc-50/50 dark:hover:bg-zinc-900/30"
                  )}
                >
                  <div
                    className={cn(
                      "relative w-full aspect-[4/3] rounded-lg mb-3 overflow-hidden border transition-colors",
                      isSelected
                        ? "border-zinc-300/50 dark:border-zinc-600/40 bg-zinc-100/40 dark:bg-zinc-800/30"
                        : "border-border/40 bg-muted/30"
                    )}
                  >
                    <opt.Illustration />
                  </div>

                  {/* Selected checkmark */}
                  {isSelected && (
                    <span className="absolute top-3 right-3 flex h-5 w-5 items-center justify-center rounded-full bg-zinc-800 dark:bg-zinc-200 text-white dark:text-zinc-900 text-[10px] font-bold shadow">
                      ✓
                    </span>
                  )}

                  <div className="space-y-1 w-full">
                    <div className="text-xs font-medium text-zinc-500 dark:text-zinc-400 tracking-wide uppercase">
                      {opt.tagline}
                    </div>
                    <div className="text-sm font-semibold leading-tight text-foreground">
                      {opt.label}
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      {opt.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          <Button
            size="lg"
            className="w-full h-12 bg-foreground text-background hover:bg-foreground/90"
            disabled={selected.length === 0 || loading}
            onClick={handleContinue}
          >
            {loading ? "Setting up..." : "Get Started"}
          </Button>

          {sandbox && (
            <div className="flex items-center justify-center gap-3">
              <span className="text-xs text-muted-foreground">
                Already a customer?
              </span>
              <Button
                asChild
                size="sm"
                className="bg-zinc-900 text-zinc-50 hover:bg-zinc-800 dark:bg-zinc-900 dark:text-zinc-50 dark:hover:bg-zinc-800"
              >
                <Link href="/login">Log In</Link>
              </Button>
            </div>
          )}
        </div>

        <div className="px-8 py-4 bg-muted/30 border-t text-center">
          <p className="text-xs text-muted-foreground">
            You can explore everything later &mdash; this just personalizes your tour.
          </p>
        </div>
      </div>
    </>
  );

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-lg p-0 gap-0 overflow-visible border-0 bg-transparent shadow-none [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Welcome to QuantWheel</DialogTitle>
        <div className="flex justify-center p-4">
          <div className="w-full max-w-lg rounded-lg overflow-hidden shadow-xl">
            {cardContent}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
